// QuickBookingWidget.jsx - Complete Clean Version with ShopCode as Primary Key
import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { 
    Dialog, 
    Button, 
    Dropdown, 
    Calendar, 
    InputNumber, 
    InputTextarea, 
    Toast,
    Message 
} from 'primereact';
import useAuth from 'hooks/useAuth';
import useApi from "hooks/useApi";
import useApp from "hooks/useApp";


const API_URL = process.env.REACT_APP_API_PATH;
const RAZORPAY_KEY_ID = process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_test_SGuxeIUsmygKAW';

const QuickBookingWidget = ({ user, onSuccess }) => {
    const [visible, setVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [services, setServices] = useState([]);
    const [allShops, setAllShops] = useState([]);
    const [nearbyShops, setNearbyShops] = useState([]);
    const [servicesLoading, setServicesLoading] = useState(false);
    const [shopsLoading, setShopsLoading] = useState(false);
    const [userLocation, setUserLocation] = useState(null);
    const [locationError, setLocationError] = useState(null);
    const [gettingAddress, setGettingAddress] = useState(false);
    const [isLoadingProfile, setIsLoadingProfile] = useState(false);
    const [profileError, setProfileError] = useState(null);
    const [userProfile, setUserProfile] = useState(null);
    const [addressLoaded, setAddressLoaded] = useState(false);
   
    const [formData, setFormData] = useState({
        serviceId: null,
        id :0,
        shopId: null,  // This will store ShopCode (string)
        pickupDate: null,
        deliveryDate: null,
        items: 5,
        instructions: '',
        pickupAddress: '',
        deliveryAddress: ''
    });
    const [errors, setErrors] = useState({});
    const toast = useRef(null);
    const auth = useAuth();
    const app = useApp();
    const api = useApi();
    const [responseMsg, setResponseMsg] = useState("");

    // Get minimum pickup date (current time + 3 hours)
    const getMinPickupDate = () => {
        const minDate = new Date();
        minDate.setHours(minDate.getHours() + 3);
        minDate.setMinutes(0);
        minDate.setSeconds(0);
        minDate.setMilliseconds(0);
        return minDate;
    };

    // Get default pickup date (current time + 3 hours, rounded to next hour)
    const getDefaultPickupDate = () => {
        const date = new Date();
        date.setHours(date.getHours() + 3);
        date.setMinutes(0);
        date.setSeconds(0);
        date.setMilliseconds(0);
        return date;
    };

    // Get minimum delivery date (pickup date + 24 hours)
    const getMinDeliveryDate = (pickupDate) => {
        if (!pickupDate) return null;
        const minDate = new Date(pickupDate);
        minDate.setHours(minDate.getHours() + 24);
        return minDate;
    };

    // Get default delivery date (pickup date + 24 hours)
    const getDefaultDeliveryDate = (pickupDate) => {
        if (!pickupDate) return null;
        const date = new Date(pickupDate);
        date.setHours(date.getHours() + 24);
        return date;
    };

    const getUserFullAddress = (userData) => {
        if (!userData) return '';
        
        const addressParts = [];
        
        // Try different possible address field names
        if (userData.address) addressParts.push(userData.address);
        if (userData.Address) addressParts.push(userData.Address);
        if (userData.city) addressParts.push(userData.city);
        if (userData.City) addressParts.push(userData.City);
        if (userData.state) addressParts.push(userData.state);
        if (userData.State) addressParts.push(userData.State);
        if (userData.zipcode) addressParts.push(userData.zipcode);
        if (userData.zipCode) addressParts.push(userData.zipCode);
        if (userData.ZipCode) addressParts.push(userData.ZipCode);
        if (userData.pincode) addressParts.push(userData.pincode);
        if (userData.Pincode) addressParts.push(userData.Pincode);
        
        // Also check for nested address object
        if (userData.addressInfo) {
            const addrInfo = userData.addressInfo;
            if (addrInfo.street) addressParts.push(addrInfo.street);
            if (addrInfo.city) addressParts.push(addrInfo.city);
            if (addrInfo.state) addressParts.push(addrInfo.state);
            if (addrInfo.zipcode || addrInfo.pincode) addressParts.push(addrInfo.zipcode || addrInfo.pincode);
        }
        
        return addressParts.filter(part => part && part.trim() !== '').join(', ');
    };

    // Fetch user profile from API using auth.userId
    const fetchUserProfile = async () => {
        if (!auth?.userId) {
            console.warn('No userId found in auth');
            // Try to get from user prop as fallback
            if (user) {
                const fullAddress = getUserFullAddress(user);
                if (fullAddress && fullAddress.trim() !== '') {
                    setFormData(prev => ({
                        ...prev,
                        pickupAddress: fullAddress,
                        deliveryAddress: fullAddress
                    }));
                    setAddressLoaded(true);
                    return { address: fullAddress };
                }
            }
            return null;
        }

        setIsLoadingProfile(true);
        setProfileError(null);
        
        try {
            console.log('Fetching user profile for userId:', auth.userId);
            
            let response;
            let profileData = null;
            
            // Method 1: Try with axios and Authorization header
            try {
                console.log('Attempting with axios + Authorization header...');
                response = await axios.get(`${API_URL}users/profile?userid=${auth.userId}`, {
                    timeout: 10000,
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${auth.accessToken || auth.token}`
                    }
                });
                console.log('Axios with auth response status:', response.status);
            } catch (axiosError) {
                console.warn('Axios with auth failed:', axiosError.message);
                
                // Method 2: Try with api instance (which has auth interceptor)
                try {
                    console.log('Attempting with api instance...');
                    response = await api.get(`${API_URL}profile?userid=${auth.userId}`);
                    console.log('API instance response status:', response.status);
                } catch (apiError) {
                    console.warn('API instance failed:', apiError.message);
                    
                    // Method 3: Try without auth (since endpoint has AllowAnonymous)
                    try {
                        console.log('Attempting without auth (AllowAnonymous)...');
                        response = await axios.get(`${API_URL}profile?userid=${auth.userId}`, {
                            timeout: 10000,
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        });
                        console.log('Axios without auth response status:', response.status);
                    } catch (noAuthError) {
                        console.error('All attempts failed:', noAuthError.message);
                        throw noAuthError;
                    }
                }
            }

            console.log('Profile API Response Data:', response.data);

            // Handle different response formats
            if (response.data) {
                // If response is an array (first record)
                if (Array.isArray(response.data) && response.data.length > 0) {
                    profileData = response.data[0];
                    console.log('Profile data from array:', profileData);
                }
                // If response has success flag with data
                else if (response.data.success && response.data.data) {
                    if (Array.isArray(response.data.data) && response.data.data.length > 0) {
                        profileData = response.data.data[0];
                    } else {
                        profileData = response.data.data;
                    }
                    console.log('Profile data from success.data:', profileData);
                }
                // If response has records
                else if (response.data.records && Array.isArray(response.data.records) && response.data.records.length > 0) {
                    profileData = response.data.records[0];
                    console.log('Profile data from records:', profileData);
                }
                // If response is the object itself
                else if (typeof response.data === 'object' && !Array.isArray(response.data)) {
                    // Check if it has address fields
                    if (response.data.address || response.data.city || response.data.state) {
                        profileData = response.data;
                        console.log('Profile data from object:', profileData);
                    }
                }
            }

            if (profileData) {
                setUserProfile(profileData);
                
                // Get address from profile
                const fullAddress = getUserFullAddress(profileData);
                console.log('Full address from profile:', fullAddress);
                
                if (fullAddress && fullAddress.trim() !== '') {
                    // Update form data with the address
                    setFormData(prev => ({
                        ...prev,
                        pickupAddress: fullAddress,
                        deliveryAddress: fullAddress
                    }));
                    setAddressLoaded(true);
                    
                    toast.current?.show({
                        severity: 'success',
                        summary: 'Address Loaded',
                        detail: 'Your address has been loaded from your profile',
                        life: 3000
                    });
                    
                    return { address: fullAddress, profile: profileData };
                } else {
                    console.warn('No address found in profile data:', profileData);
                    // Try to use user prop as fallback
                    if (user) {
                        const fallbackAddress = getUserFullAddress(user);
                        if (fallbackAddress && fallbackAddress.trim() !== '') {
                            setFormData(prev => ({
                                ...prev,
                                pickupAddress: fallbackAddress,
                                deliveryAddress: fallbackAddress
                            }));
                            setAddressLoaded(true);
                            return { address: fallbackAddress };
                        }
                    }
                }
            } else {
                console.warn('No profile data found in response:', response.data);
                setProfileError('No profile data found');
                
                // Try fallback - use user prop if available
                if (user) {
                    const fullAddress = getUserFullAddress(user);
                    if (fullAddress && fullAddress.trim() !== '') {
                        setFormData(prev => ({
                            ...prev,
                            pickupAddress: fullAddress,
                            deliveryAddress: fullAddress
                        }));
                        setAddressLoaded(true);
                        return { address: fullAddress };
                    }
                }
            }
            
            return null;
        } catch (error) {
            console.error('Error fetching user profile:', error);
            setProfileError(error.message || 'Failed to fetch profile');
            
            // Try fallback - use user prop if available
            if (user) {
                const fullAddress = getUserFullAddress(user);
                if (fullAddress && fullAddress.trim() !== '') {
                    setFormData(prev => ({
                        ...prev,
                        pickupAddress: fullAddress,
                        deliveryAddress: fullAddress
                    }));
                    setAddressLoaded(true);
                    return { address: fullAddress };
                }
            }
            
            // Check if auth has user data
            if (auth?.user) {
                const fullAddress = getUserFullAddress(auth.user);
                if (fullAddress && fullAddress.trim() !== '') {
                    setFormData(prev => ({
                        ...prev,
                        pickupAddress: fullAddress,
                        deliveryAddress: fullAddress
                    }));
                    setAddressLoaded(true);
                    return { address: fullAddress };
                }
            }
            
            return null;
        } finally {
            setIsLoadingProfile(false);
        }
    };

    // Reverse geocoding to get address from coordinates
    const getAddressFromCoords = async (latitude, longitude) => {
        setGettingAddress(true);
        try {
            // Using OpenStreetMap Nominatim API (free, no API key required)
            const response = await axios.get(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`
            );
            
            if (response.data && response.data.display_name) {
                const address = response.data.display_name;
                setFormData(prev => ({
                    ...prev,
                    pickupAddress: address
                }));
                setAddressLoaded(true);
                
                toast.current?.show({
                    severity: 'success',
                    summary: 'Location Found',
                    detail: 'Pickup address automatically set from your location',
                    life: 3000
                });
                return address;
            }
        } catch (error) {
            console.error('Error getting address:', error);
            // Fallback: Use coordinates as address
            const fallbackAddress = `${latitude}, ${longitude}`;
            setFormData(prev => ({
                ...prev,
                pickupAddress: fallbackAddress
            }));
            setAddressLoaded(true);
            
            toast.current?.show({
                severity: 'warn',
                summary: 'Partial Location',
                detail: 'Could not get full address, using coordinates',
                life: 3000
            });
            return fallbackAddress;
        } finally {
            setGettingAddress(false);
        }
    };

    // Get current location and set pickup address
    const getCurrentLocation = async () => {
        // FIRST PRIORITY: Try to get address from user profile
        if (auth?.userId || user) {
            const profileResult = await fetchUserProfile();
            if (profileResult?.address) {
                console.log('✅ Address loaded from profile:', profileResult.address);
                return; // Exit - we already have address from profile
            }
        }

        // SECOND PRIORITY: If no profile address, get from geolocation
        if (!navigator.geolocation) {
            toast.current?.show({
                severity: 'error',
                summary: 'Not Supported',
                detail: 'Geolocation is not supported by your browser',
                life: 5000
            });
            return;
        }

        navigator.geolocation.getCurrentPosition(
            async (position) => {
                const { latitude, longitude } = position.coords;
                
                console.log("Getting location from geolocation:", latitude, longitude);
                
                // Store coordinates for distance calculation
                setUserLocation({
                    lat: latitude,
                    lng: longitude
                });
                
                // Get address from coordinates
                await getAddressFromCoords(latitude, longitude);
            },
            (error) => {
                console.error('Geolocation error:', error);
                let errorMessage = "Unable to retrieve location";
                
                switch(error.code) {
                    case error.PERMISSION_DENIED:
                        errorMessage = "Location access denied. Please enable location services.";
                        break;
                    case error.POSITION_UNAVAILABLE:
                        errorMessage = "Location information is unavailable.";
                        break;
                    case error.TIMEOUT:
                        errorMessage = "Location request timed out.";
                        break;
                    default:
                        errorMessage = "Unable to retrieve your location.";
                }
                
                // If location fails, try to use user address from props
                if (user?.address) {
                    setFormData(prev => ({
                        ...prev,
                        pickupAddress: user.address
                    }));
                    setAddressLoaded(true);
                    toast.current?.show({
                        severity: 'info',
                        summary: 'Using Profile Address',
                        detail: 'Using address from your profile',
                        life: 3000
                    });
                } else {
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Location Error',
                        detail: errorMessage,
                        life: 5000
                    });
                }
                setLocationError(errorMessage);
            },
            {
                enableHighAccuracy: true,
                timeout: 10000,
            }
        );
    };

    // Calculate distance between two coordinates using Haversine formula
    const calculateDistance = (lat1, lon1, lat2, lon2) => {
        if (!lat1 || !lon1 || !lat2 || !lon2) return Infinity;
        
        const R = 6371;
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a = 
            Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
            Math.sin(dLon/2) * Math.sin(dLon/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        return R * c;
    };

    // Filter shops within radius and sort by distance
    const filterNearbyShops = (userLat, userLng, shops) => {
        const shopsWithDistance = shops
            .filter(shop => {
                if (shop.status !== 'Active') return false;
                if (!shop.latitude || !shop.longitude) return false;
                
                const distance = calculateDistance(
                    userLat, 
                    userLng, 
                    parseFloat(shop.latitude), 
                    parseFloat(shop.longitude)
                );
                
                const maxRadius = shop.serviceRadius || 25;
                return distance <= maxRadius;
            })
            .map(shop => ({
                ...shop,
                distance: calculateDistance(
                    userLat, 
                    userLng, 
                    parseFloat(shop.latitude), 
                    parseFloat(shop.longitude)
                )
            }))
            .sort((a, b) => a.distance - b.distance);
        
        return shopsWithDistance;
    };

    // Get user's current location for shop filtering
    const getUserLocationForShops = () => {
        return new Promise((resolve, reject) => {
            if (!navigator.geolocation) {
                reject(new Error('Geolocation is not supported by your browser'));
                return;
            }
            
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const location = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude
                    };
                    setUserLocation(location);
                    setLocationError(null);
                    resolve(location);
                },
                (error) => {
                    let errorMessage = 'Unable to get your location. ';
                    switch(error.code) {
                        case error.PERMISSION_DENIED:
                            errorMessage += 'Please enable location access to find nearby shops.';
                            break;
                        case error.POSITION_UNAVAILABLE:
                            errorMessage += 'Location information is unavailable.';
                            break;
                        case error.TIMEOUT:
                            errorMessage += 'Location request timed out.';
                            break;
                        default:
                            errorMessage += 'Please enter your address manually.';
                    }
                    setLocationError(errorMessage);
                    reject(new Error(errorMessage));
                },
                {
                    enableHighAccuracy: true,
                    timeout: 10000,
                    maximumAge: 0
                }
            );
        });
    };

    // Fetch all shops from database
    const fetchAllShops = async () => {
        setShopsLoading(true);
        try {
            const token =  localStorage.getItem('token');
            const response = await axios.get(`${API_URL}customer/shops`, {
                headers: { 'Authorization': `Bearer ${auth.accessToken}` }
            });
            
            const shopsData = Array.isArray(response.data) ? response.data : [];
            setAllShops(shopsData);
            return shopsData;
        } catch (error) {
            console.error('Error fetching shops:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load shops',
                life: 3000
            });
            return [];
        } finally {
            setShopsLoading(false);
        }
    };

    // Fetch services from database
    useEffect(() => {
        const fetchServices = async () => {
            setServicesLoading(true);
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(`${API_URL}customer/service`, {
                    headers: { 'Authorization': `Bearer ${auth.accessToken}` }
                });
                const servicesData = Array.isArray(response.data) ? response.data : [];
                setServices(servicesData);
                
                if (servicesData && servicesData.length > 0) {
                    const defaultPickup = getDefaultPickupDate();
                    const defaultDelivery = getDefaultDeliveryDate(defaultPickup);
                    setFormData(prev => ({
                        ...prev,
                        serviceId: servicesData[0].id,
                        pickupDate: defaultPickup,
                        deliveryDate: defaultDelivery
                    }));
                }
            } catch (error) {
                console.error('Error fetching services:', error);
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to load services',
                    life: 3000
                });
            } finally {
                setServicesLoading(false);
            }
        };
        
        fetchServices();
        fetchAllShops();
        
        // Auto-fetch profile address when component mounts
        if (auth?.userId || user) {
            fetchUserProfile();
        }
    }, []);

    // Filter nearby shops when user location or all shops change
    useEffect(() => {
        if (userLocation && allShops.length > 0) {
            const nearby = filterNearbyShops(userLocation.lat, userLocation.lng, allShops);
            setNearbyShops(nearby);
            
            if (nearby.length > 0) {
                setFormData(prev => ({
                    ...prev,
                    shopId: nearby[0].shopCode  // Store ShopCode (string)
                }));
            }
        }
    }, [userLocation, allShops]);

    // Get selected shop object by ShopCode
    const getSelectedShop = () => {
        if (!formData.shopId) return null;
        return nearbyShops.find(shop => shop.shopCode === formData.shopId);
    };

    const selectedShop = getSelectedShop();

    // Handle dialog open
    const handleDialogOpen = async () => {
        setVisible(true);
        setLocationError(null);
        
        // Reset form but keep address
        const defaultPickup = getDefaultPickupDate();
        const defaultDelivery = getDefaultDeliveryDate(defaultPickup);
        
        setFormData(prev => ({
            ...prev,
            serviceId: services[0]?.id || null,
            pickupDate: defaultPickup,
            deliveryDate: defaultDelivery,
            items: 5,
            instructions: '',
            // Keep existing address if available
        }));
        
        // FIRST: Try to get address from profile (highest priority)
        if (auth?.userId || user) {
            await fetchUserProfile();
        }
        
        // If address is already loaded from profile, skip geolocation
        if (addressLoaded && formData.pickupAddress) {
            console.log('✅ Using profile address:', formData.pickupAddress);
            // Still get location for shops
            try {
                await getUserLocationForShops();
            } catch (error) {
                console.error('Shop location error:', error);
            }
            return;
        }
        
        // SECOND: If no profile address, get from geolocation
        if (allShops.length > 0) {
            try {
                // Get address for pickup
                await getCurrentLocation();
                // Get location for shops filtering
                await getUserLocationForShops();
            } catch (error) {
                console.error('Location error:', error);
            }
        }
    };

    // Handle shop selection - stores ShopCode
    const handleShopChange = (e) => {
        console.log("Selected ShopCode:", e.value); // Should be string like "SHP001"
        
        setFormData(prev => ({
            ...prev,
            shopId: e.value  // Store ShopCode string
        }));
        
        setErrors(prev => ({
            ...prev,
            shopId: ''
        }));
    };

    const handleInputChange = (field, value) => {
        if (field === 'pickupDate') {
            const minPickup = getMinPickupDate();
            
            // Check if selected date is valid
            if (value < minPickup) {
                toast.current?.show({
                    severity: 'warn',
                    summary: 'Invalid Pickup Time',
                    detail: `Pickup must be at least 3 hours from now. Minimum: ${minPickup.toLocaleString()}`,
                    life: 3000
                });
                return;
            }
            
            // Auto-update delivery date when pickup changes
            const defaultDelivery = getDefaultDeliveryDate(value);
            setFormData(prev => ({
                ...prev,
                pickupDate: value,
                deliveryDate: defaultDelivery
            }));
            
            // Clear delivery date error if exists
            if (errors.deliveryDate) {
                setErrors(prev => ({
                    ...prev,
                    deliveryDate: ''
                }));
            }
            return;
        }
        
        if (field === 'deliveryDate') {
            if (formData.pickupDate) {
                const minDelivery = getMinDeliveryDate(formData.pickupDate);
                if (value < minDelivery) {
                    toast.current?.show({
                        severity: 'warn',
                        summary: 'Invalid Delivery Time',
                        detail: `Delivery must be at least 24 hours after pickup. Minimum: ${minDelivery.toLocaleString()}`,
                        life: 3000
                    });
                    return;
                }
            }
        }
        
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.serviceId) {
            newErrors.serviceId = 'Service is required';
        }
        
        if (!formData.shopId) {
            newErrors.shopId = 'Please select a nearby shop';
        }
        
        if (!formData.pickupDate) {
            newErrors.pickupDate = 'Pickup date is required';
        } else {
            const minPickup = getMinPickupDate();
            if (formData.pickupDate < minPickup) {
                newErrors.pickupDate = `Pickup must be at least 3 hours from now (minimum: ${minPickup.toLocaleString()})`;
            }
        }
        
        if (!formData.deliveryDate) {
            newErrors.deliveryDate = 'Delivery date is required';
        } else if (formData.pickupDate) {
            const minDelivery = getMinDeliveryDate(formData.pickupDate);
            if (formData.deliveryDate < minDelivery) {
                newErrors.deliveryDate = `Delivery must be at least 24 hours after pickup (minimum: ${minDelivery.toLocaleString()})`;
            }
        }
        
        if (!formData.items || formData.items < 1 || formData.items > 50) {
            newErrors.items = 'Items must be between 1 and 50';
        }
        
        if (!formData.pickupAddress) {
            newErrors.pickupAddress = 'Pickup address is required';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const calculateTotal = () => {
        const selectedService = services.find(s => s.id === formData.serviceId);
        const basePrice = selectedService?.price || 0;
        const itemPrice = 2.5;
        return basePrice + (formData.items * itemPrice);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            toast.current?.show({
                severity: 'error',
                summary: 'Validation Error',
                detail: 'Please fix the errors in the form',
                life: 3000
            });
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const totalAmount = calculateTotal();
            
            const bookingData = {
                serviceId: parseInt(formData.serviceId, 10),
                shopCode: formData.shopId,
                pickupDate: formData.pickupDate.toISOString(),
                deliveryDate: formData.deliveryDate.toISOString(),
                pickupAddress: formData.pickupAddress || user?.address || '',
                deliveryAddress: formData.deliveryAddress || user?.address || '',
                totalAmount: parseFloat(totalAmount),
                totalItems: parseInt(formData.items, 10),
                specialInstructions: formData.instructions || ''
            };

            console.log("Sending booking data:", bookingData);

            const response = await axios.post(`${API_URL}customer/qbooking`, bookingData, {
                headers: { 
                    'Authorization': `Bearer ${auth.accessToken}`,
                    'Content-Type': 'application/json'
                }
            });
            
            toast.current?.show({
                severity: 'success',
                summary: 'Booking Confirmed!',
                detail: `Booking #${response.data.bookingReference} created successfully. Total: ₹${totalAmount.toFixed(2)}`,
                life: 5000
            });

            // Call sendWhatsApp with the response data
            await sendWhatsApp(response.data);

            // Reset form
            const minPickup = getMinPickupDate();
            const minDelivery = getMinDeliveryDate(minPickup);
            setFormData(prev => ({
                ...prev,
                serviceId: services[0]?.id || null,
                shopId: null,
                pickupDate: minPickup,
                deliveryDate: minDelivery,
                items: 5,
                instructions: '',
                // Keep the address
            }));
            setErrors({});
            setVisible(false);
            
            if (onSuccess) onSuccess();
        } catch (error) {
            console.error('Booking error:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Booking Failed',
                detail: error.response?.data?.message || 'Failed to create booking. Please try again.',
                life: 5000
            });
        } finally {
            setLoading(false);
        }
    };

    // Define sendWhatsApp outside handleSubmit, but inside the component
    const sendWhatsApp = async (bookingResponse) => {
        try {
            const res = await axios.post(
                `${API_URL}whats/send-invoice`,
                {
                    BookingId: bookingResponse.Id,
                    CustomerPhone: bookingResponse.CustomerPhone,
                    CustomerName: bookingResponse.CustomerName || "Customer",
                    Amount: bookingResponse.TotalAmount,
                    PaymentId: "N/A"
                },
                {
                    headers: {
                        Authorization: `Bearer ${auth.accessToken}`,
                        "Content-Type": "application/json"
                    }
                }
            );

            if (res.data.success) {
                console.log("✅ WhatsApp message sent:", res.data.message);
                toast.current?.show({
                    severity: 'info',
                    summary: 'WhatsApp Notification',
                    detail: 'Invoice sent via WhatsApp',
                    life: 3000
                });
            } else {
                console.error("❌ WhatsApp error:", res.data.message);
                // Don't show error toast for WhatsApp failure to not confuse user
            }
        } catch (error) {
            console.error("WhatsApp send error:", error);
            // Silent fail for WhatsApp - booking is already confirmed
        }
    };

    // Add a button to manually refresh location
    const refreshLocation = () => {
        getCurrentLocation();
        getUserLocationForShops();
    };

    const selectedService = services.find(s => s.id === formData.serviceId);
    const minPickupDate = getMinPickupDate();
    const minDeliveryDate = formData.pickupDate ? getMinDeliveryDate(formData.pickupDate) : null;

    // Shop dropdown templates
    const shopOptionTemplate = (shop) => {
        if (!shop) return null;
        return (
            <div className="flex flex-column p-2">
                <div className="font-semibold">{shop.shopName}</div>
                <div className="text-sm text-gray-600">
                    {shop.distance ? `${shop.distance.toFixed(1)} km away` : 'Distance unavailable'}
                    {shop.rating ? ` • ⭐ ${shop.rating.toFixed(1)}` : ''}
                    {shop.address ? ` • ${shop.address.split(',')[0]}` : ''}
                </div>
                <div className="text-xs text-gray-400 mt-1">
                    Shop Code: {shop.shopCode}
                </div>
            </div>
        );
    };

    const selectedShopTemplate = (option) => {
        if (!option) return <span>Select a shop</span>;
        
        return (
            <div className="flex align-items-center">
                <span className="font-medium">{option.shopName}</span>
                {option.distance && (
                    <span className="text-sm text-gray-500 ml-2">
                        ({option.distance.toFixed(1)} km)
                    </span>
                )}
                <span className="text-xs text-gray-400 ml-2">
                    [{option.shopCode}]
                </span>
            </div>
        );
    };

    return (
        <>
            <Toast ref={toast} position="top-right" />
            
            <Button 
                label="Quick Book" 
                icon="pi pi-bolt" 
                className="p-button-success p-button-rounded"
                onClick={handleDialogOpen}
                tooltip="Quick laundry booking"
                tooltipOptions={{ position: 'bottom' }}
            />

            <Dialog 
                header="Quick Laundry Booking" 
                visible={visible} 
                style={{ width: '600px', maxWidth: '90vw' }}
                onHide={() => {
                    setVisible(false);
                    setErrors({});
                    setLocationError(null);
                }}
                draggable={false}
                resizable={false}
            >
                <form onSubmit={handleSubmit} className="p-fluid">
                    {/* Shop Selection */}
                    <div className="field mb-3">
                        <label htmlFor="shop" className="block font-medium mb-2">
                            Select Shop <span className="text-red-500">*</span>
                        </label>
                        
                        {locationError && (
                            <Message 
                                severity="warn" 
                                text={locationError}
                                className="mb-2"
                                closable
                            />
                        )}
                        
                        <Dropdown
                            id="shop"
                            value={formData.shopId}
                            options={nearbyShops}
                            optionLabel="shopName"
                            optionValue="shopCode"  // Use ShopCode as value
                            onChange={handleShopChange}
                            itemTemplate={shopOptionTemplate}
                            valueTemplate={selectedShopTemplate}
                            placeholder="Select a shop"
                            loading={shopsLoading}
                        />
                        
                        {errors.shopId && (
                            <small className="p-error block mt-1">{errors.shopId}</small>
                        )}
                        
                        {/* Shop Details Card */}
                        {selectedShop && (
                            <div className="mt-3 p-3 bg-blue-50 border-round shadow-sm">
                                <div className="flex justify-content-between align-items-start mb-2">
                                    <div>
                                        <i className="pi pi-store mr-2 text-primary"></i>
                                        <strong className="text-lg">{selectedShop.shopName}</strong>
                                        <div className="text-xs text-gray-500 mt-1">
                                            Shop Code: {selectedShop.shopCode}
                                        </div>
                                    </div>
                                    {selectedShop.rating && (
                                        <span className="bg-yellow-100 px-2 py-1 border-round text-sm font-semibold">
                                            ⭐ {selectedShop.rating.toFixed(1)}
                                        </span>
                                    )}
                                </div>
                                
                                <div className="text-sm text-gray-700 mt-2">
                                    <div className="mb-1">
                                        <i className="pi pi-map-marker mr-1 text-primary"></i>
                                        <strong>Address:</strong> {selectedShop.address}, {selectedShop.city}
                                    </div>
                                    
                                    <div className="grid mt-2">
                                        <div className="col-6">
                                            <i className="pi pi-compass mr-1 text-primary"></i>
                                            <strong>Distance:</strong> {selectedShop.distance?.toFixed(1)} km
                                        </div>
                                        <div className="col-6">
                                            <i className="pi pi-phone mr-1 text-primary"></i>
                                            <strong>Contact:</strong> {selectedShop.contactPhone}
                                        </div>
                                    </div>
                                    
                                    {selectedShop.currentLoad >= selectedShop.capacityPerDay && (
                                        <div className="mt-2 text-warning text-sm bg-yellow-50 p-2 border-round">
                                            <i className="pi pi-exclamation-triangle mr-1"></i>
                                            <strong>Note:</strong> Shop is at full capacity today
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        
                        {/* No Shops Message */}
                        {!locationError && nearbyShops.length === 0 && !shopsLoading && allShops.length > 0 && (
                            <div className="mt-3 p-3 bg-yellow-50 border-round">
                                <i className="pi pi-info-circle mr-1 text-warning"></i>
                                <span className="text-sm">No shops found within 25km of your location.</span>
                                <Button 
                                    label="Refresh Location" 
                                    icon="pi pi-refresh" 
                                    className="p-button-text p-button-sm mt-2"
                                    onClick={refreshLocation}
                                    type="button"
                                />
                            </div>
                        )}
                        
                        {/* Loading Shops */}
                        {shopsLoading && (
                            <div className="mt-3 p-3 text-center">
                                <i className="pi pi-spin pi-spinner mr-2"></i>
                                <span>Finding shops near you...</span>
                            </div>
                        )}
                    </div>

                    {/* Service Selection */}
                    <div className="field mb-3">
                        <label htmlFor="service" className="block font-medium mb-2">
                            Service Type <span className="text-red-500">*</span>
                        </label>
                        <Dropdown
                            id="service"
                            value={formData.serviceId}
                            options={services}
                            optionLabel="name"
                            optionValue="id"
                            placeholder={servicesLoading ? "Loading services..." : "Select Service"}
                            onChange={(e) => handleInputChange('serviceId', e.value)}
                            className={errors.serviceId ? 'p-invalid' : ''}
                            disabled={servicesLoading}
                        />
                        {errors.serviceId && (
                            <small className="p-error block mt-1">{errors.serviceId}</small>
                        )}
                    </div>

                    {/* Date Selection */}
                    <div className="grid mb-3">
                        <div className="col-6">
                            <label htmlFor="pickupDate" className="block font-medium mb-2">
                                Pickup Date <span className="text-red-500">*</span>
                            </label>
                            <Calendar
                                id="pickupDate"
                                value={formData.pickupDate}
                                onChange={(e) => handleInputChange('pickupDate', e.value)}
                                showIcon
                                dateFormat="dd/mm/yy"
                                showTime
                                hourFormat="12"
                                minDate={minPickupDate}
                                className={errors.pickupDate ? 'p-invalid' : ''}
                                disabled={servicesLoading}
                            />
                            {errors.pickupDate && (
                                <small className="p-error block mt-1">{errors.pickupDate}</small>
                            )}
                            <small className="text-gray-500 block mt-1">
                                Minimum 3 hours from now
                            </small>
                        </div>
                        <div className="col-6">
                            <label htmlFor="deliveryDate" className="block font-medium mb-2">
                                Delivery Date <span className="text-red-500">*</span>
                            </label>
                            <Calendar
                                id="deliveryDate"
                                value={formData.deliveryDate}
                                onChange={(e) => handleInputChange('deliveryDate', e.value)}
                                showIcon
                                dateFormat="dd/mm/yy"
                                showTime
                                hourFormat="12"
                                minDate={minDeliveryDate}
                                className={errors.deliveryDate ? 'p-invalid' : ''}
                                disabled={servicesLoading || !formData.pickupDate}
                            />
                            {errors.deliveryDate && (
                                <small className="p-error block mt-1">{errors.deliveryDate}</small>
                            )}
                            <small className="text-gray-500 block mt-1">
                                Minimum 24 hours after pickup
                            </small>
                        </div>
                    </div>

                    {/* Number of Items */}
                    <div className="field mb-3">
                        <label htmlFor="items" className="block font-medium mb-2">
                            Number of Items <span className="text-red-500">*</span>
                        </label>
                        <InputNumber
                            id="items"
                            value={formData.items}
                            onValueChange={(e) => handleInputChange('items', e.value)}
                            min={1}
                            max={50}
                            showButtons
                            buttonLayout="horizontal"
                            incrementButtonIcon="pi pi-plus"
                            decrementButtonIcon="pi pi-minus"
                            className={errors.items ? 'p-invalid' : ''}
                            disabled={servicesLoading}
                        />
                        {errors.items && (
                            <small className="p-error block mt-1">{errors.items}</small>
                        )}
                    </div>

                    {/* Pickup Address */}
                    <div className="field mb-3">
                        <label htmlFor="pickupAddress" className="block font-medium mb-2">
                            Pickup Address <span className="text-red-500">*</span>
                        </label>
                        <div className="flex gap-2">
                            <InputTextarea
                                id="pickupAddress"
                                value={formData.pickupAddress}
                                onChange={(e) => handleInputChange('pickupAddress', e.target.value)}
                                rows={2}
                                placeholder={isLoadingProfile ? "Loading profile..." : (gettingAddress ? "Getting your location..." : (user?.address || "Enter pickup address"))}
                                className="w-full"
                                disabled={servicesLoading || gettingAddress || isLoadingProfile}
                            />
                            <Button 
                                icon="pi pi-map-marker" 
                                className="p-button-outlined"
                                onClick={refreshLocation}
                                type="button"
                                tooltip="Get Location"
                                loading={gettingAddress || isLoadingProfile}
                                disabled={gettingAddress || isLoadingProfile}
                            />
                        </div>
                        {errors.pickupAddress && (
                            <small className="p-error block mt-1">{errors.pickupAddress}</small>
                        )}
                        {isLoadingProfile && (
                            <small className="text-primary block mt-1">
                                <i className="pi pi-spin pi-spinner mr-1"></i>
                                Loading profile address...
                            </small>
                        )}
                        {!formData.pickupAddress && !gettingAddress && !isLoadingProfile && (
                            <small className="text-gray-500 block mt-1">
                                Click the location button to auto-fill your current address
                            </small>
                        )}
                        {gettingAddress && (
                            <small className="text-primary block mt-1">
                                <i className="pi pi-spin pi-spinner mr-1"></i>
                                Fetching your location...
                            </small>
                        )}
                        {formData.pickupAddress && !isLoadingProfile && (
                            <small className="text-success block mt-1">
                                <i className="pi pi-check-circle mr-1"></i>
                                Address loaded successfully
                            </small>
                        )}
                    </div>

                    {/* Special Instructions */}
                    <div className="field mb-4">
                        <label htmlFor="instructions" className="block font-medium mb-2">
                            Special Instructions
                        </label>
                        <InputTextarea
                            id="instructions"
                            value={formData.instructions}
                            onChange={(e) => handleInputChange('instructions', e.target.value)}
                            rows={2}
                            placeholder="Any special requirements, stains, or preferences..."
                            className="w-full"
                            disabled={servicesLoading}
                        />
                    </div>

                    {/* Price Summary */}
                    {selectedService && (
                        <div className="mb-4 p-3 surface-50 border-round">
                            <h5 className="mt-0 mb-2">Booking Summary</h5>
                            <div className="grid">
                                <div className="col-6">
                                    <div className="text-sm text-gray-600">Service</div>
                                    <div className="font-medium">{selectedService.name}</div>
                                </div>
                                <div className="col-6 text-right">
                                    <div className="text-sm text-gray-600">Base Price</div>
                                    <div>₹{selectedService.price?.toFixed(2)}</div>
                                </div>
                            </div>
                            <div className="grid mt-2">
                                <div className="col-6">
                                    <div className="text-sm text-gray-600">Items ({formData.items})</div>
                                    <div>@ $2.50 each</div>
                                </div>
                                <div className="col-6 text-right">
                                    <div className="text-sm text-gray-600">Items Total</div>
                                    <div>₹{(formData.items * 2.5).toFixed(2)}</div>
                                </div>
                            </div>
                            <div className="flex justify-content-between align-items-center mt-3 pt-3 border-top-1 surface-border">
                                <div className="font-bold">Total Amount</div>
                                <div className="text-xl font-bold text-primary">₹{calculateTotal().toFixed(2)}</div>
                            </div>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex justify-content-end gap-2 mt-4">
                        <Button 
                            label="Cancel" 
                            className="p-button-text" 
                            onClick={() => setVisible(false)}
                            disabled={loading || servicesLoading}
                            type="button"
                        />
                        <Button 
                            label={loading ? 'Processing...' : 'Confirm Booking'} 
                            icon={loading ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
                            type="submit"
                            disabled={loading || servicesLoading || shopsLoading || nearbyShops.length === 0}
                            loading={loading}
                            className="p-button-success"
                        />
                    </div>
                </form>
            </Dialog>
        </>
    );
};

export default QuickBookingWidget;
